import React, { lazy, Suspense } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
} from 'react-router-dom';
import { useSelector } from 'react-redux';
import OnboardingLoader from '../components/OnboardingLoader/OnboardingLoader';

const AmenitiesPage = lazy(
  () => import('../pages/SettingsPage/Pages/AmenitiesPage/AmenitiesPage')
);
const AllBookingsPage = lazy(
  () => import('../pages/AllBookingsPage/AllBookingsPage')
);
const CalendarPage = lazy(() => import('../pages/CalendarPage/CalendarPage'));
const CourtsPage = lazy(() => import('../pages/CourtsPage/CourtsPage'));
const SingleCustomerPage = lazy(
  () => import('../pages/SingleCustomerPage/SingleCustomerPage')
);
const GetStartedPage = lazy(
  () => import('../pages/GetStartedPage/GetStartedPage')
);
const InvitationPage = lazy(
  () => import('../pages/InvitationPage/InvitationPage')
);
const NotFoundPage = lazy(() => import('../pages/NotFoundPage/NotFoundPage'));
const PersonalAccountPage = lazy(
  () => import('../pages/PersonalAccountPage/PersonalAccountPage')
);
const SettingsPage = lazy(() => import('../pages/SettingsPage/SettingsPage'));
const SignInPage = lazy(() => import('../pages/SignInPage/SignInPage'));
const SignUpPage = lazy(() => import('../pages/SignUpPage/SignUpPage'));
const SharedLayout = lazy(() => import('../pages/SharedLayout/SharedLayout'));
const SubscriptionPage = lazy(
  () => import('../pages/SubscriptionPage/SubscriptionPage')
);
const SingleTrainerPage = lazy(
  () => import('../pages/SingleTrainerPage/SingleTrainerPage')
);
const ProtectedRoute = lazy(
  () => import('../pages/ProtectedRoute/ProtectedRoute')
);
const CaptchaFormPage = lazy(
  () => import('../pages/CaptchaFormPage/CaptchaFormPage')
);
const AdminsSettingsPage = lazy(
  () =>
    import('../pages/SettingsPage/Pages/AdminsSettingsPage/AdminsSettingsPage')
);
const ScheduleSettingsPage = lazy(
  () =>
    import(
      '../pages/SettingsPage/Pages/ScheduleSettingsPage/ScheduleSettingsPage'
    )
);
const GeneralSettingsPage = lazy(
  () =>
    import(
      '../pages/SettingsPage/Pages/GeneralSettingsPage/GeneralSettingsPage'
    )
);
const BillingSettingsPage = lazy(
  () =>
    import(
      '../pages/SettingsPage/Pages/BillingSettingsPage/BillingSettingsPage'
    )
);
const BookingRulesPage = lazy(
  () => import('../pages/SettingsPage/Pages/BookingRulesPage/BookingRulesPage')
);
const SportsSelectorPage = lazy(
  () =>
    import('../pages/SettingsPage/Pages/SportsSelectorPage/SportsSelectorPage')
);
const LogsSettingsPage = lazy(
  () => import('../pages/SettingsPage/Pages/LogsSettingsPage/LogsSettingsPage')
);
const InventoryPage = lazy(
  () => import('../pages/InventoryPage/InventoryPage')
);
const CustomerTrainerPage = lazy(
  () => import('../pages/CustomerTrainerPage/CustomerTrainerPage')
);
const NewPasswordPage = lazy(
  () => import('../pages/NewPasswordPage/NewPasswordPage')
);
const SuccessPasswordPage = lazy(
  () => import('../pages/SuccessPasswordPage/SuccessPasswordPage')
);
const ResetPasswordPage = lazy(
  () => import('../pages/ResetPasswordPage/ResetPasswordPage')
);
const Mobile = lazy(() => import('../pages/SettingsPage/Mobile'));

const RootRouters = () => {
  const user = useSelector((state) => state.auth.user);
  const isSettingsComplete = useSelector(
    (state) => state.settings.isSettingsComplete
  );

  const hasCompletedSettings = isSettingsComplete;

  return (
    <BrowserRouter>
      <Suspense fallback={<OnboardingLoader />}>
        <Routes>
          <Route path="/login" element={<SignInPage />} />
          <Route path="/signup" element={<SignUpPage />} />
          <Route
            path="/newpassword/:resetToken"
            element={<NewPasswordPage />}
          />
          <Route path="/successpassword" element={<SuccessPasswordPage />} />
          <Route path="/resetpassword" element={<ResetPasswordPage />} />
          <Route path="/captcha" element={<CaptchaFormPage />} />
          <Route path="/invitation/:token" element={<InvitationPage />} />
          <Route
            path="/"
            element={
              <ProtectedRoute user={user}>
                <SharedLayout />
              </ProtectedRoute>
            }
          >
            <Route
              index
              element={
                hasCompletedSettings ? (
                  <Navigate to="/calendar" replace />
                ) : (
                  <GetStartedPage />
                )
              }
            />
            <Route path="/settings/menu" element={<Mobile />} />
            <Route path="/settings" element={<SettingsPage />}>
              <Route
                index
                element={<Navigate to="/settings/general" replace />}
              />
              <Route path="general" element={<GeneralSettingsPage />} />
              <Route path="sports" element={<SportsSelectorPage />} />
              <Route path="schedule" element={<ScheduleSettingsPage />} />
              <Route path="admins" element={<AdminsSettingsPage />} />
              <Route path="billing" element={<BillingSettingsPage />} />
              <Route path="booking_rules" element={<BookingRulesPage />} />
              <Route path="amenities" element={<AmenitiesPage />} />
              <Route path="logs" element={<LogsSettingsPage />} />
            </Route>
            {/* Сторінки доступні під час онбордингу */}
            <Route path="/courts" element={<CourtsPage />} />
            <Route path="/trainers" element={<CustomerTrainerPage />} />
            <Route
              path="/trainers/:trainerId"
              element={<SingleTrainerPage />}
            />
            <Route path="/inventory" element={<InventoryPage />} />
            {/* Сторінки доступні лише після завершення онбордингу */}
            <Route
              element={
                hasCompletedSettings ? <Outlet /> : <Navigate to="/" replace />
              }
            >
              <Route path="/statistics" element={<AllBookingsPage />} />
              <Route path="/calendar" element={<CalendarPage />} />
              <Route path="/customers" element={<CustomerTrainerPage />} />
              <Route
                path="/customers/:customerId"
                element={<SingleCustomerPage />}
              />
              <Route path="/account" element={<PersonalAccountPage />} />
              <Route path="/subscription" element={<SubscriptionPage />} />
            </Route>
          </Route>
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
};

export default RootRouters;
